import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import {
  SectionHeading,
  Subheading as SubheadingBase,
} from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-6.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "images/svg-decorator-blob-7.svg";
import i18n from "helpers/i18n";

const HeaderContainer = tw.div`w-full flex flex-col items-center`;
const Subheading = tw(SubheadingBase)`mb-4`;
const Heading = tw(SectionHeading)`w-full`;
const Description = tw(SectionDescription)`w-full text-center`;

const PlansContainer = tw.div`flex justify-center flex-col md:flex-row items-center md:items-start relative`;
const Plan = styled.div`
  ${tw`w-full max-w-80 mt-8 md:mr-12 md:last:mr-0 text-center px-8 rounded-lg relative text-gray-900 bg-white flex flex-col shadow-raised`}

  ${(props) =>
    props.featured &&
    css`
      ${tw`border-2 border-gray-200 shadow-none`}
    `}
`;

const PlanHeader = styled.div`
  ${tw`flex flex-col leading-relaxed py-8 -mx-8 bg-gray-100 rounded-t-lg`}
  .name {
    ${tw`font-bold text-xl`}
  }
  .price {
    ${tw`font-bold text-4xl sm:text-5xl my-1`}
  }
  .slash {
    ${tw`text-xl text-gray-500`}
  }
  .duration {
    ${tw`lowercase text-gray-500 font-medium tracking-widest`}
  }
  .mainFeature {
    ${tw`text-gray-500 text-sm font-medium tracking-wide`}
  }
`;
const PlanFeatures = styled.div`
  ${tw`flex flex-col -mx-8 px-8 py-8 flex-1 text-sm`}
  .feature {
    ${tw`mt-5 first:mt-0 font-semibold text-gray-500`}
  }
`;

const PlanAction = tw.div`px-4 pb-8`;
const PlanPreviewLink = styled(PrimaryButtonBase)`
  ${tw`inline-block rounded-full tracking-wider py-4 w-full text-sm hover:shadow-xl transform hocus:translate-x-px hocus:-translate-y-px focus:shadow-outline`}
`;

const DecoratorBlob1 = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none -z-20 absolute left-0 bottom-0 h-64 w-64 opacity-25 transform -translate-x-2/3 -translate-y-1/2`}
`;
const DecoratorBlob2 = styled(SvgDecoratorBlob2)`
  ${tw`pointer-events-none -z-20 absolute right-0 top-0 h-64 w-64 opacity-25 transform translate-x-2/3 translate-y-1/2 fill-current text-teal-300`}
`;

export default ({
  subheading = "Pricing",
  heading = "Flexible Plans.",
  description = i18n.t(
    "Giá dưới đây là giá tham khảo cho 1 năm, bằng VND. Chọn gói để xem hạn mức, rồi liên hệ hỗ trợ xác nhận giá, thời hạn và cách cấp mã trước khi thanh toán."
  ),
  primaryButtonText = i18n.t("Xem gói & liên hệ"),
  planDurations = [
    {
      text: i18n.t("Tháng"),
      switcherText: i18n.t("1 tháng"),
    },
    {
      text: i18n.t("Năm"),
      switcherText: i18n.t("1 năm"),
    },
  ],
}) => {
  const defaultPlans = [
    {
      plan: "Unlimited basic",
      name: "✨ " + i18n.t("Nhiều Page · Cơ bản"),
      durationPrices: ["₫200.000", "₫1.990.000"],
      mainFeature: i18n.t("Cho nhiều Facebook Page"),
      features: [
        "✨ " + i18n.t("Không Giới Hạn Facebook Page"),
        "✓ " + i18n.t("Hỗ trợ Pancake"),

        "✓ " + i18n.t("Liên hệ hỗ trợ"),
        `50.000 ${i18n.t("tin nhắn / tháng")}`,
        "2 IP",
      ],
    },
    {
      plan: "Unlimited standard",
      name: "✨ " + i18n.t("Nhiều Page · Tiêu chuẩn"),
      durationPrices: ["₫350.000", "₫3.490.000"],
      mainFeature: i18n.t("Cho nhiều Facebook Page"),
      features: [
        "✨ " + i18n.t("Không Giới Hạn Facebook Page"),
        "✓ " + i18n.t("Hỗ trợ Pancake"),

        "✓ " + i18n.t("Liên hệ hỗ trợ"),
        `300.000 ${i18n.t("tin nhắn / tháng")}`,
        "3 IP",
      ],
      featured: true,
    },
    {
      plan: "Unlimited premium",
      name: "✨ " + i18n.t("Nhiều Page · Cao cấp"),
      durationPrices: ["₫500.000", "₫4.990.000"],
      mainFeature: i18n.t("Cho nhiều Facebook Page"),
      features: [
        "✨ " + i18n.t("Không Giới Hạn Facebook Page"),
        "✓ " + i18n.t("Hỗ trợ Pancake"),

        "✓ " + i18n.t("Liên hệ hỗ trợ"),
        "✓ " + i18n.t("Không giới hạn tin nhắn trong gói"),
        "5 IP",
      ],
      //featured: true
    },
  ];

  const pagePlans = [
    {
      plan: "Page small",
      name: i18n.t("Page nhỏ"),
      mainFeature: i18n.t("Cho 1 Facebook Page"),
      durationPrices: ["₫60.000", "₫590.000"],
      features: [
        i18n.t("1 Facebook Page"),
        "✓ " + i18n.t("Hỗ trợ Pancake"),

        "✓ " + i18n.t("Liên hệ hỗ trợ"),
        `15.000 ${i18n.t("tin nhắn / tháng")}`,
      ],
    },
    {
      plan: "Page medium",
      name: i18n.t("Page vừa"),
      mainFeature: i18n.t("Cho 1 Facebook Page"),
      durationPrices: ["₫90.000", "₫890.000"],
      features: [
        i18n.t("1 Facebook Page"),
        "✓ " + i18n.t("Hỗ trợ Pancake"),

        "✓ " + i18n.t("Liên hệ hỗ trợ"),
        `45.000 ${i18n.t("tin nhắn / tháng")}`,
      ],
      featured: true,
    },
    {
      plan: "Page large",
      name: i18n.t("Page lớn"),
      mainFeature: i18n.t("Cho 1 Facebook Page"),
      durationPrices: ["₫120.000", "₫1.190.000"],
      features: [
        i18n.t("1 Facebook Page"),
        "✓ " + i18n.t("Hỗ trợ Pancake"),

        "✓ " + i18n.t("Liên hệ hỗ trợ"),
        "✓ " + i18n.t("Không giới hạn tin nhắn trong gói"),
      ],
      //featured: true
    },
  ];

  const msgPlans = [];
  // const msgPlans = [
  //   {
  //     plan: "Messages 10,000",
  //     name: `10.000 ${i18n.t("tin nhắn")}`,
  //     durationPrices: [i18n.t("₫200.000"), i18n.t("₫1.990.000")],

  //     features: [
  //       "✨ " + i18n.t("Không Giới Hạn Facebook Page"),
  //       "✓ " + i18n.t("Hỗ trợ Pancake"),

  //       "✓ " + i18n.t("Nhóm Hỗ Trợ Riêng"),
  //       `10.000 ${i18n.t("tin nhắn")}`,
  //       "⚠️ " + i18n.t("Hết giới hạn tin nhắn, ngưng sử dụng"),
  //     ],
  //   },
  //   {
  //     plan: "Messages 50,000",
  //     name: `50.000 ${i18n.t("tin nhắn")}`,
  //     durationPrices: [i18n.t("₫500.000"), i18n.t("₫3.490.000")],
  //     features: [
  //       "✨ " + i18n.t("Không Giới Hạn Facebook Page"),
  //       "✓ " + i18n.t("Hỗ trợ Pancake"),

  //       "✓ " + i18n.t("Nhóm Hỗ Trợ Riêng"),
  //       `50.000 ${i18n.t("tin nhắn")}`,
  //       "⚠️ " + i18n.t("Hết giới hạn tin nhắn, ngưng sử dụng"),
  //     ],
  //     featured: true,
  //   },
  //   {
  //     plan: "Messages 120,000",
  //     name: `120.000 ${i18n.t("tin nhắn")}`,
  //     durationPrices: [i18n.t("₫1.000.000"), i18n.t("₫4.990.000")],
  //     features: [
  //       "✨ " + i18n.t("Không Giới Hạn Facebook Page"),
  //       "✓ " + i18n.t("Hỗ trợ Pancake"),

  //       "✓ " + i18n.t("Nhóm Hỗ Trợ Riêng"),
  //       `120.000 ${i18n.t("tin nhắn")}`,
  //       "⚠️ " + i18n.t("Hết giới hạn tin nhắn, ngưng sử dụng"),
  //     ],
  //     //featured: true
  //   },
  // ];

  const activeDurationIndex = 1;
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const ref = localStorage.getItem("ref") || urlParams.get("ref");
  if (ref) {
    localStorage.setItem("ref", ref);
  }

  const getLink = (plan) => {
    const params = new URLSearchParams({
      plan: plan.plan,
      duration: "1 year",
      lang: localStorage.getItem("language") || "vi",
    });
    if (ref) params.set("ref", ref);
    const gatewayUrl = process.env.REACT_APP_GATEWAY_URL || "https://gateway.re-marketing.click";
    const purchaseUrl = new URL("/purchase", gatewayUrl);
    purchaseUrl.search = params.toString();
    return purchaseUrl.toString();
  };

  return (
    <Container id="pricing">
      <ContentWithPaddingXl>
        <HeaderContainer>
          {subheading && <Subheading>{subheading}</Subheading>}
          <Heading>{heading}</Heading>
          {description && <Description>{description}</Description>}
          <p tw="mt-4 text-sm text-gray-600 text-center max-w-3xl">
            {i18n.t("Không giới hạn trong gói là hạn mức của phần mềm; khả năng gửi vẫn phụ thuộc vào tài khoản và các giới hạn của Facebook hoặc Pancake.")}
          </p>
        </HeaderContainer>
        <PlansContainer>
          {pagePlans.map((plan, index) => (
            <Plan key={index} featured={plan.featured}>
              <PlanHeader>
                <span
                  className="priceAndDuration block mx-5"
                  style={{
                    display: "block",
                    margin: "0 20px",
                  }}
                >
                  <span className="price">
                    {plan.durationPrices[activeDurationIndex]}
                  </span>
                  <br />
                  <span className="slash"> / </span>
                  <span className="duration">
                    {planDurations[activeDurationIndex].text}
                  </span>
                </span>
                <span className="name">{plan.name}</span>
                <span className="mainFeature">
                  {plan.mainFeature}
                </span>
              </PlanHeader>
              <PlanFeatures>
                {plan.features.map((feature, index) => (
                  <span key={index} className="feature">
                    {feature}
                  </span>
                ))}
              </PlanFeatures>
              <PlanAction>
                <PlanPreviewLink
                  as="a"
                  href={getLink(plan)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ background: "#000" }}
                >
                  {primaryButtonText}
                </PlanPreviewLink>
              </PlanAction>
            </Plan>
          ))}
        </PlansContainer>

        <PlansContainer>
          {defaultPlans.map((plan, index) => (
            <Plan key={index} featured={plan.featured}>
              <PlanHeader>
                <span
                  className="priceAndDuration block mx-5"
                  style={{
                    display: "block",
                    margin: "0 20px",
                  }}
                >
                  <span className="price">
                    {plan.durationPrices[activeDurationIndex]}
                  </span>
                  <br />
                  <span className="slash"> / </span>
                  <span className="duration">
                    {planDurations[activeDurationIndex].text}
                  </span>
                </span>
                <span className="name">{plan.name}</span>
                <span className="mainFeature">{plan.mainFeature}</span>
              </PlanHeader>
              <PlanFeatures>
                {plan.features.map((feature, index) => (
                  <span key={index} className="feature">
                    {feature}
                  </span>
                ))}
              </PlanFeatures>
              <PlanAction>
                <PlanPreviewLink as="a" href={getLink(plan)} target="_blank" rel="noopener noreferrer">
                  {primaryButtonText}
                </PlanPreviewLink>
              </PlanAction>
            </Plan>
          ))}
        </PlansContainer>

        <PlansContainer>
          {msgPlans.map((plan, index) => (
            <Plan key={index} featured={plan.featured}>
              <PlanHeader>
                <span
                  className="priceAndDuration block mx-5"
                  style={{
                    display: "block",
                    margin: "0 20px",
                  }}
                >
                  <span className="price">
                    {plan.durationPrices[activeDurationIndex]}
                  </span>
                  <br />
                  <span className="slash"> / </span>
                  <span className="duration">{plan.name}</span>
                </span>
                <span className="mainFeature">{plan.mainFeature}</span>
              </PlanHeader>
              <PlanFeatures>
                {plan.features.map((feature, index) => (
                  <span key={index} className="feature">
                    {feature}
                  </span>
                ))}
              </PlanFeatures>
              <PlanAction>
                <PlanPreviewLink
                  as="a"
                  href={getLink(plan)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ background: "#000" }}
                >
                  {primaryButtonText}
                </PlanPreviewLink>
              </PlanAction>
            </Plan>
          ))}
        </PlansContainer>
      </ContentWithPaddingXl>
      <DecoratorBlob1 />
      <DecoratorBlob2 />
    </Container>
  );
};
